// Shared hub chrome — frozen once by Decision Brief (or hub bootstrap) so
// Scoring Rationale / ROI Model never rebuild a different header.

import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import type { EffortTag } from '../components/EffortTagPill';
import type { HubKpi } from '../components/hub/EAUseCaseHubShell';
import type { VerdictKey } from '../theme/decisionTokens';

export interface HubChromeState {
  useCaseId: string;
  title: string;
  category: string;
  rank: number | null;
  portfolioSize: number;
  effortTag: EffortTag | null;
  verdict: VerdictKey;
  decisionContent: React.ReactNode;
  bannerFooter?: React.ReactNode;
  kpis: HubKpi;
  freshnessLabel?: string | null;
  freshnessIsLive?: boolean;
  freshnessTitle?: string;
  recommendationDerived?: boolean;
}

interface HubChromeContextValue {
  chrome: HubChromeState | null;
  /**
   * Publish chrome.
   * - Default: only fills when empty (bootstrap).
   * - `{ force: true }`: Brief overwrites with canonical profile chrome.
   * Skips React updates when the identity fingerprint is unchanged.
   */
  setChrome: (next: HubChromeState, opts?: { force?: boolean }) => void;
  clearChrome: (useCaseId?: string | null) => void;
  exportHandlerRef: React.MutableRefObject<(() => void) | null>;
  /** Register Brief's export handler without triggering context re-renders. */
  setExportHandler: (fn: (() => void) | null) => void;
}

const HubChromeContext = createContext<HubChromeContextValue | null>(null);

function chromeIdentityKey(c: HubChromeState): string {
  return [
    c.useCaseId,
    c.title,
    c.category,
    c.rank ?? '',
    c.portfolioSize,
    c.effortTag ?? '',
    c.verdict,
    String(c.kpis.annualBenefit),
    String(c.kpis.npv),
    String(c.kpis.timeToValue),
    String(c.kpis.compositeScore),
    String(c.kpis.portfolioRank),
    c.freshnessLabel ?? '',
    c.recommendationDerived ? '1' : '0',
  ].join('|');
}

export function HubChromeProvider({ children }: { children: React.ReactNode }) {
  const [chrome, setChromeState] = useState<HubChromeState | null>(null);
  const chromeKeyRef = useRef<string | null>(null);
  const exportHandlerRef = useRef<(() => void) | null>(null);

  const setChrome = useCallback((next: HubChromeState, opts?: { force?: boolean }) => {
    const key = chromeIdentityKey(next);
    setChromeState((prev) => {
      // Bootstrap: do not clobber existing chrome for this use case.
      if (!opts?.force && prev && prev.useCaseId === next.useCaseId) {
        return prev;
      }
      // Same fingerprint → no update (breaks setState loops from unstable effect deps).
      if (prev && chromeKeyRef.current === key) {
        return prev;
      }
      chromeKeyRef.current = key;
      return next;
    });
  }, []);

  const clearChrome = useCallback((useCaseId?: string | null) => {
    setChromeState((prev) => {
      if (!useCaseId) {
        chromeKeyRef.current = null;
        return null;
      }
      if (prev && prev.useCaseId === String(useCaseId)) {
        chromeKeyRef.current = null;
        return null;
      }
      return prev;
    });
    exportHandlerRef.current = null;
  }, []);

  const setExportHandler = useCallback((fn: (() => void) | null) => {
    exportHandlerRef.current = fn;
  }, []);

  const value = useMemo(
    () => ({
      chrome,
      setChrome,
      clearChrome,
      exportHandlerRef,
      setExportHandler,
    }),
    [chrome, setChrome, clearChrome, setExportHandler],
  );

  return <HubChromeContext.Provider value={value}>{children}</HubChromeContext.Provider>;
}

export function useHubChrome(): HubChromeContextValue {
  const ctx = useContext(HubChromeContext);
  if (!ctx) {
    const noopRef = { current: null } as React.MutableRefObject<(() => void) | null>;
    return {
      chrome: null,
      setChrome: () => undefined,
      clearChrome: () => undefined,
      exportHandlerRef: noopRef,
      setExportHandler: () => undefined,
    };
  }
  return ctx;
}
